import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import ThemeProvider from '@/uptk/core/packages/theme/ThemeProvider';
import createToolkitTheme from '@/uptk/core/packages/theme/createToolkitTheme';
import {
  deleteCurrentTenantTheme,
  fetchCurrentTenantThemeCatalog,
  fetchCurrentTenantTheme,
  fetchUserThemePreference,
  type TenantThemeApiError,
  updateCurrentTenantTheme,
  updateUserThemePreference,
} from '@/uptk/core/api/TenantThemeApi';
import {
  apply_theme_css_variables,
} from '@/uptk/core/theme/theme_css_variables';
import {
  inject_tenant_preset_stylesheet,
  remove_tenant_preset_stylesheet,
} from '@/uptk/core/theme/tenant_preset_stylesheet';
import {
  create_persistable_theme_configuration,
  copy_theme_configuration,
  create_toolkit_palette_overrides,
  default_tenant_theme_configuration,
  resolve_theme_mode_configuration,
} from '@/uptk/core/theme/theme_runtime';
import {
  apply_document_theme_state,
  is_public_theme_route,
  resolve_theme_mode_for_path,
  theme_mode_changed_event_name,
} from '@/uptk/core/theme/theme_mode';
import {
  getAuthContextState,
  subscribeAuthContextState,
} from '@/uptk/core/api/auth_context_store';
import { resolveSaasContext } from '@/uptk/core/api/saasContext';
import { subscribeTenantOverrideState } from '../api/tenant_override_store';
import * as themeActions from '@/redux/actions/themeActions';
import type {
  ResolvedThemeModeConfiguration,
  ThemeCatalogItem,
  TenantThemeResponse,
  TenantThemeConfiguration,
  ThemeMode,
  ThemeScope,
} from '@/types/theme_configuration';

type RootState = {
  selectedTheme?: string | null;
};

type ThemeSession = {
  setTheme?: (theme: unknown) => void;
};

type ThemeConfigurationContextValue = {
  tenantId: string;
  tenantTheme: TenantThemeResponse | null;
  configuration: TenantThemeConfiguration;
  effectiveConfiguration: TenantThemeConfiguration;
  defaultConfiguration: TenantThemeConfiguration;
  themeCatalog: ThemeCatalogItem[];
  selectedThemeKey: string | null;
  selectedTheme: ThemeCatalogItem | null;
  activeMode: ThemeMode;
  activeScope: ThemeScope;
  activeTheme: ResolvedThemeModeConfiguration;
  isLoading: boolean;
  errorMessage: string | null;
  refreshTheme: () => Promise<TenantThemeResponse | null>;
  saveThemeConfiguration: (
    configuration: TenantThemeConfiguration
  ) => Promise<TenantThemeResponse | null>;
  resetThemeConfiguration: () => Promise<boolean>;
  setSelectedThemeKey: (themeKey: string | null) => Promise<boolean>;
  savePreset: (
    name: string,
    configuration: TenantThemeConfiguration,
    themeKey?: string | null
  ) => Promise<ThemeCatalogItem | null>;
  deletePreset: (themeKey: string) => Promise<boolean>;
};

const ThemeConfigurationContext = React.createContext<ThemeConfigurationContextValue | undefined>(
  undefined
);

const get_theme_error_message = (error: unknown) => {
  const themeError = error as TenantThemeApiError | undefined;
  const detailMessage = themeError?.details?.filter(Boolean).join(' ');
  if (detailMessage) {
    return detailMessage;
  }

  switch (themeError?.status) {
    case 400:
      return themeError.message || 'The tenant theme payload is invalid. Review the theme settings and try again.';
    case 401:
      return 'Your session has expired. Please sign in again before managing the tenant theme.';
    case 403:
      return 'You do not have permission to manage the tenant theme.';
    case 404:
      return 'No tenant theme override was found. Default platform theme values are currently active.';
    case 409:
      return 'The tenant theme has changed on the server. Refresh the configurator before saving again.';
    case 422:
      return themeError.message || 'The tenant theme contains invalid values. Review the highlighted tokens and try again.';
    case 500:
      return 'The server failed to process the tenant theme request. Please try again later.';
    default:
      return themeError?.message || 'Unable to load tenant theme configuration.';
  }
};

const resolve_tenant_id = () => {
  return resolveSaasContext({
    authContext: getAuthContextState(),
  }).tenantId;
};

type ThemeConfigurationProviderProps = {
  children: React.ReactNode;
  pathname: string;
  profileName?: string;
  envTheme: Record<string, string>;
  session?: ThemeSession;
};

export function ThemeConfigurationProvider({
  children,
  pathname,
  profileName,
  envTheme,
  session,
}: ThemeConfigurationProviderProps) {
  const dispatch = useDispatch();
  const selectedThemeModePreference = useSelector((state: RootState) => state.selectedTheme);
  const actionsApi = useMemo(
    () =>
      bindActionCreators(
        {
          saveTheme: themeActions.saveTheme,
          saveSelectedTheme: themeActions.saveSelectedTheme,
        },
        dispatch
      ),
    [dispatch]
  );

  const [tenantId, setTenantId] = useState<string>(() => resolve_tenant_id());
  const [tenantTheme, setTenantThemeState] = useState<TenantThemeResponse | null>(null);
  /**
   * UPT-1968 — la version du thème telle que le SERVEUR la connaît à l'instant.
   *
   * L'enregistrement est un verrou optimiste : on renvoie la version qu'on
   * croit avoir. Or deux écritures se suivent dans le même tour — le thème du
   * tenant, puis le préréglage sélectionné — et la seconde lisait la version
   * capturée par sa fermeture, donc celle d'AVANT la première. Elle partait
   * systématiquement périmée : le serveur répondait « modifié par un autre
   * utilisateur », le préréglage n'était jamais mis à jour, et l'écran
   * affichait en même temps l'erreur et le succès. Une référence suit la
   * dernière version connue, hors du cycle de rendu.
   */
  const latestVersionRef = useRef<number | null>(null);
  const setTenantTheme = useCallback((next: TenantThemeResponse | null) => {
    latestVersionRef.current = next?.version ?? null;
    setTenantThemeState(next);
  }, []);
  const [themeCatalog, setThemeCatalog] = useState<ThemeCatalogItem[]>([]);
  const [selectedThemeKey, setSelectedThemeKeyState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [themeModeSignal, setThemeModeSignal] = useState<number>(0);

  useEffect(() => {
    return subscribeAuthContextState((authContext) => {
      setTenantId(
        resolveSaasContext({
          authContext,
        }).tenantId
      );
    });
  }, []);

  useEffect(() => {
    return subscribeTenantOverrideState(() => {
      setTenantId(resolve_tenant_id());
    });
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'selectedTheme' || event.key === 'publicTheme') {
        setThemeModeSignal((previous) => previous + 1);
      }
    };

    const handleThemeModeChanged = () => {
      setThemeModeSignal((previous) => previous + 1);
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(theme_mode_changed_event_name, handleThemeModeChanged as EventListener);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(theme_mode_changed_event_name, handleThemeModeChanged as EventListener);
    };
  }, []);

  const resolve_selected_theme_key = useCallback(
    (
      themeResponse: TenantThemeResponse | null,
      catalog: ThemeCatalogItem[],
      userThemeKey: string | null
    ) => {
      const fromUser = userThemeKey && userThemeKey.trim().length > 0 ? userThemeKey.trim() : null;
      if (fromUser) {
        return fromUser;
      }

      const fromTheme = themeResponse?.selectedThemeKey;
      if (fromTheme && fromTheme.trim().length > 0) {
        return fromTheme.trim();
      }

      const selectedFromCatalog = catalog.find((entry) => entry.selected)?.themeKey ?? null;
      if (selectedFromCatalog && selectedFromCatalog.trim().length > 0) {
        return selectedFromCatalog.trim();
      }

      return null;
    },
    []
  );

  const refreshTheme = useCallback(async () => {
    if (!tenantId) {
      setTenantTheme(null);
      setThemeCatalog([]);
      setSelectedThemeKeyState(null);
      setErrorMessage(null);
      return null;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const [themeResponse, catalogResponse, userPreference] = await Promise.all([
        fetchCurrentTenantTheme({ tenantId }),
        fetchCurrentTenantThemeCatalog({ tenantId }),
        fetchUserThemePreference({ tenantId }),
      ]);

      const nextCatalog =
        catalogResponse?.themes ??
        userPreference?.availableThemes ??
        themeResponse?.availableThemes ??
        [];
      const nextSelectedThemeKey = resolve_selected_theme_key(
        themeResponse,
        nextCatalog,
        userPreference?.themeKey ?? null
      );

      setTenantTheme(themeResponse);
      setThemeCatalog(nextCatalog);
      setSelectedThemeKeyState(nextSelectedThemeKey);
      return themeResponse;
    } catch (error) {
      setTenantTheme(null);
      setThemeCatalog([]);
      setSelectedThemeKeyState(null);
      setErrorMessage(get_theme_error_message(error));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [resolve_selected_theme_key, tenantId]);

  useEffect(() => {
    void refreshTheme();
  }, [refreshTheme]);

  useEffect(() => {
    return () => {
      remove_tenant_preset_stylesheet();
    };
  }, []);

  const activeScope = is_public_theme_route(pathname) ? 'public' : 'portal';
  const activeMode = useMemo(() => {
    const currentSignal = themeModeSignal;
    void currentSignal;
    return resolve_theme_mode_for_path(pathname, selectedThemeModePreference ?? null);
  }, [pathname, selectedThemeModePreference, themeModeSignal]);

  const configuration = useMemo(
    () => copy_theme_configuration(tenantTheme?.configuration ?? default_tenant_theme_configuration),
    [tenantTheme?.configuration]
  );

  const selectedTheme = useMemo(
    () =>
      themeCatalog.find((themeOption) => themeOption.themeKey === selectedThemeKey) ??
      themeCatalog.find((themeOption) => themeOption.selected) ??
      null,
    [selectedThemeKey, themeCatalog]
  );

  const effectiveConfiguration = useMemo(
    () => copy_theme_configuration(configuration),
    [configuration]
  );

  const activeTheme = useMemo(
    () => resolve_theme_mode_configuration(effectiveConfiguration, activeScope, activeMode),
    [activeMode, activeScope, effectiveConfiguration]
  );

  const theme = useMemo(() => {
    const paletteOverrides = create_toolkit_palette_overrides(activeTheme);
    return createToolkitTheme(activeMode, profileName, envTheme, {
      palette: paletteOverrides,
    });
  }, [activeMode, activeTheme, envTheme, profileName]);

  useEffect(() => {
    apply_document_theme_state(pathname, activeMode, tenantId);
    apply_theme_css_variables(activeTheme);
    inject_tenant_preset_stylesheet(tenantId, effectiveConfiguration);
    session?.setTheme?.(theme);
    actionsApi.saveTheme(theme);
    if (!is_public_theme_route(pathname)) {
      actionsApi.saveSelectedTheme(activeMode);
    }
  }, [actionsApi, activeMode, activeTheme, effectiveConfiguration, pathname, session, tenantId, theme]);

  const saveThemeConfiguration = useCallback(
    async (nextConfiguration: TenantThemeConfiguration) => {
      if (!tenantId) {
        setErrorMessage('Tenant context is missing. Unable to save the tenant theme.');
        return null;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const persistableConfiguration = create_persistable_theme_configuration(nextConfiguration);
        const response = await updateCurrentTenantTheme(
          {
            version: latestVersionRef.current ?? tenantTheme?.version ?? null,
            configuration: persistableConfiguration,
          },
          { tenantId }
        );
        setTenantTheme(response);
        return response;
      } catch (error) {
        setErrorMessage(get_theme_error_message(error));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId, tenantTheme?.version]
  );

  const resetThemeConfiguration = useCallback(async () => {
    if (!tenantId) {
      setErrorMessage('Tenant context is missing. Unable to reset the tenant theme.');
      return false;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      await deleteCurrentTenantTheme({ tenantId });
      setTenantTheme(null);
      return true;
    } catch (error) {
      setErrorMessage(get_theme_error_message(error));
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  const setSelectedThemeKey = useCallback(
    async (themeKey: string | null) => {
      if (!tenantId) {
        setErrorMessage('Tenant context is missing. Unable to update the active theme preset.');
        return false;
      }

      const normalizedThemeKey = themeKey && themeKey.trim().length > 0 ? themeKey.trim() : 'platform-default';

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const preferenceResponse = await updateUserThemePreference(
          { themeKey: normalizedThemeKey },
          { tenantId }
        );

        const themeResponse = await fetchCurrentTenantTheme({ tenantId });
        setTenantTheme(themeResponse);
        setThemeCatalog(preferenceResponse.availableThemes ?? []);
        setSelectedThemeKeyState(preferenceResponse.themeKey);
        return true;
      } catch (error) {
        setErrorMessage(get_theme_error_message(error));
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId]
  );

  const to_theme_key = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);

  const savePreset = useCallback(
    async (
      name: string,
      presetConfiguration: TenantThemeConfiguration,
      themeKey?: string | null
    ) => {
      if (!tenantId) {
        setErrorMessage('Tenant context is missing. Unable to save the theme preset.');
        return null;
      }

      const trimmedName = name.trim();
      if (trimmedName.length === 0) {
        setErrorMessage('Enter a preset name before saving the theme preset.');
        return null;
      }

      const normalizedThemeKey =
        themeKey && themeKey.trim().length > 0 ? themeKey.trim() : to_theme_key(trimmedName);
      if (normalizedThemeKey.length === 0) {
        setErrorMessage('Unable to generate a valid theme key from the preset name.');
        return null;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        const persistableConfiguration = create_persistable_theme_configuration(presetConfiguration);
        await updateCurrentTenantTheme(
          {
            version: latestVersionRef.current ?? tenantTheme?.version ?? null,
            themeKey: normalizedThemeKey,
            themeName: trimmedName,
            configuration: persistableConfiguration,
          },
          { tenantId }
        );
        await refreshTheme();
        return (
          themeCatalog.find((entry) => entry.themeKey === normalizedThemeKey) ?? {
            themeKey: normalizedThemeKey,
            themeName: trimmedName,
            selected: true,
            tenantDefault: false,
            source: 'TENANT_OVERRIDE',
          }
        );
      } catch (error) {
        setErrorMessage(get_theme_error_message(error));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshTheme, tenantId, tenantTheme?.version, themeCatalog]
  );

  const deletePreset = useCallback(
    async (themeKey: string) => {
      if (!tenantId) {
        setErrorMessage('Tenant context is missing. Unable to delete the theme preset.');
        return false;
      }

      const normalizedThemeKey = themeKey.trim();
      if (normalizedThemeKey.length === 0 || normalizedThemeKey === 'platform-default') {
        setErrorMessage('The selected preset cannot be deleted.');
        return false;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        await deleteCurrentTenantTheme({ tenantId, themeKey: normalizedThemeKey });
        await refreshTheme();
        return true;
      } catch (error) {
        setErrorMessage(get_theme_error_message(error));
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshTheme, tenantId]
  );

  const contextValue = useMemo<ThemeConfigurationContextValue>(
    () => ({
      tenantId,
      tenantTheme,
      configuration,
      effectiveConfiguration,
      defaultConfiguration: copy_theme_configuration(default_tenant_theme_configuration),
      themeCatalog,
      selectedThemeKey,
      selectedTheme,
      activeMode,
      activeScope,
      activeTheme,
      isLoading,
      errorMessage,
      refreshTheme,
      saveThemeConfiguration,
      resetThemeConfiguration,
      setSelectedThemeKey,
      savePreset,
      deletePreset,
    }),
    [
      activeMode,
      activeScope,
      activeTheme,
      configuration,
      deletePreset,
      errorMessage,
      effectiveConfiguration,
      isLoading,
      selectedTheme,
      selectedThemeKey,
      themeCatalog,
      refreshTheme,
      resetThemeConfiguration,
      savePreset,
      saveThemeConfiguration,
      setSelectedThemeKey,
      tenantId,
      tenantTheme,
    ]
  );

  return (
    <ThemeConfigurationContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeConfigurationContext.Provider>
  );
}

export const useThemeConfiguration = () => {
  const context = React.useContext(ThemeConfigurationContext);
  if (!context) {
    throw new Error('useThemeConfiguration must be used inside ThemeConfigurationProvider');
  }
  return context;
};
